import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import * as api from '../services/api';
import type {
  Criteria,
  CriteriaPurpose,
  CriterionTranscriptionAssignment,
  Essay,
  EssayPurpose,
  Purpose,
  Transcription,
} from '../types';

export type AssignmentCompletionFilter = 'not_done' | 'done' | 'all';
export type AssignmentDateFilter = 'all' | 'today' | 'last7' | 'last30' | 'custom';

export interface LabellerAssignmentsDrawerProps {
  open: boolean;
  onClose: () => void;
  assignments: CriterionTranscriptionAssignment[];
  criteria: Criteria[];
  transcriptions: Transcription[];
  essays: Essay[];
  purposes: Purpose[];
  criteriaPurposes: CriteriaPurpose[];
  essayPurposes: EssayPurpose[];
  onSelectAssignment: (criterion: Criteria, transcription: Transcription) => void;
  onAssignmentsUpdated: (updatedAssignments: CriterionTranscriptionAssignment[]) => void;
  showNotification?: (message: string, severity: 'success' | 'error') => void;
}

export const formatShortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: '2-digit' });

export function LabellerAssignmentsDrawer({
  open,
  onClose,
  assignments,
  criteria,
  transcriptions,
  essays,
  purposes,
  criteriaPurposes,
  essayPurposes,
  onSelectAssignment,
  onAssignmentsUpdated,
  showNotification,
}: LabellerAssignmentsDrawerProps) {
  const [completionFilter, setCompletionFilter] = useState<AssignmentCompletionFilter>('not_done');
  const [dateFilter, setDateFilter] = useState<AssignmentDateFilter>('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // Criteria collapse state: all criteria collapsed by default
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());

  // Pre-index criteria and transcriptions for quick lookup
  const criteriaMap = useMemo(() => new Map(criteria.map(c => [c.id, c])), [criteria]);
  const transcriptionsMap = useMemo(
    () => new Map(transcriptions.map(t => [t.id, t])),
    [transcriptions]
  );
  const essaysMap = useMemo(() => new Map(essays.map(e => [e.id, e])), [essays]);
  const purposesMap = useMemo(() => new Map(purposes.map(p => [p.id, p])), [purposes]);

  // Overall counts for user's assignments
  const totalCount = assignments.length;
  const doneCount = useMemo(() => assignments.filter(a => a.done).length, [assignments]);
  const notDoneCount = totalCount - doneCount;
  const overallPct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  // Helper to describe transcription
  const describeTranscription = useCallback(
    (transcriptionId: string, criteriaId: string) => {
      const transcription = transcriptionsMap.get(transcriptionId);
      if (!transcription) {
        return {
          transcriptNumber: transcriptionId.slice(0, 8),
          purpose: '—',
          transcription: null,
        };
      }
      const essay = essaysMap.get(transcription.essayId);

      const purposeIds = criteriaPurposes
        .filter(cp => cp.criteriaId === criteriaId)
        .map(cp => cp.purposeId);
      const essayPurposeId = essayPurposes.find(
        ep => ep.essayId === transcription.essayId && purposeIds.includes(ep.purposeId)
      )?.purposeId;
      let purposeName = essayPurposeId ? purposesMap.get(essayPurposeId)?.name : null;
      if (!purposeName && essay) {
        const firstEp = essayPurposes.find(ep => ep.essayId === essay.id);
        if (firstEp) {
          purposeName = purposesMap.get(firstEp.purposeId)?.name;
        }
      }

      const purpose = purposeName || '—';
      const transcriptNumber = essay ? String(essay.paper_number) : transcription.id.slice(0, 8);

      return { transcriptNumber, purpose, transcription };
    },
    [transcriptionsMap, essaysMap, criteriaPurposes, essayPurposes, purposesMap]
  );

  // Date filtering predicate
  const matchesDateFilter = useCallback(
    (assignedDateIso: string): boolean => {
      if (dateFilter === 'all') return true;
      const assignedDate = new Date(assignedDateIso);
      const now = new Date();
      if (dateFilter === 'today') {
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return assignedDate >= startOfToday;
      }
      if (dateFilter === 'last7') {
        const cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return assignedDate >= cutoff;
      }
      if (dateFilter === 'last30') {
        const cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return assignedDate >= cutoff;
      }
      if (dateFilter === 'custom') {
        if (customStartDate && assignedDate < new Date(`${customStartDate}T00:00:00`)) return false;
        if (customEndDate && assignedDate > new Date(`${customEndDate}T23:59:59`)) return false;
        return true;
      }
      return true;
    },
    [dateFilter, customStartDate, customEndDate]
  );

  // Build criterion-grouped rows
  const { groupedData, criteriaCounts } = useMemo(() => {
    const groups = new Map<
      string,
      {
        criterionId: string;
        criterionName: string;
        granularity: string;
        allAssignments: CriterionTranscriptionAssignment[];
        filteredAssignments: CriterionTranscriptionAssignment[];
      }
    >();

    const query = searchQuery.trim().toLowerCase();

    // 1. Group assignments matching date filter by criterion
    assignments.forEach(assignment => {
      const crit = criteriaMap.get(assignment.criteriaId);
      const critName = crit?.name || 'Unknown Criterion';
      const granularity = crit?.granularity || 'sentence';

      let group = groups.get(assignment.criteriaId);
      if (!group) {
        group = {
          criterionId: assignment.criteriaId,
          criterionName: critName,
          granularity,
          allAssignments: [],
          filteredAssignments: [],
        };
        groups.set(assignment.criteriaId, group);
      }

      if (matchesDateFilter(assignment.assignedDate)) {
        group.allAssignments.push(assignment);
      }
    });

    // 2. Compute group summaries
    const groupSummaries = Array.from(groups.values())
      .filter(g => g.allAssignments.length > 0)
      .map(g => {
        const total = g.allAssignments.length;
        const groupDone = g.allAssignments.filter(a => a.done).length;
        const groupNotDone = total - groupDone;
        const groupPct = total > 0 ? Math.round((groupDone / total) * 100) : 0;
        return {
          ...g,
          total,
          groupDone,
          groupNotDone,
          groupPct,
        };
      });

    // Counts for criteria filter tabs
    const notDoneCriteriaCount = groupSummaries.filter(g => g.groupNotDone > 0).length;
    const doneCriteriaCount = groupSummaries.filter(
      g => g.total > 0 && g.groupDone === g.total
    ).length;
    const totalCriteriaCount = groupSummaries.length;

    // 3. Filter criteria groups according to completionFilter:
    // - 'not_done': show any criterion with >0 open or started assignments (groupNotDone > 0)
    // - 'done': show any criterion with 100% done assignments (total > 0 && groupDone === total)
    // - 'all': show all criteria
    const matchingGroups = groupSummaries.filter(g => {
      if (completionFilter === 'not_done') {
        return g.groupNotDone > 0;
      }
      if (completionFilter === 'done') {
        return g.total > 0 && g.groupDone === g.total;
      }
      return true;
    });

    // 4. For matching criteria, show all assignments within it (filtered by searchQuery if present)
    const result = matchingGroups
      .map(g => {
        let matchingAssignments = g.allAssignments;
        if (query) {
          const critMatches = g.criterionName.toLowerCase().includes(query);
          if (!critMatches) {
            matchingAssignments = g.allAssignments.filter(assignment => {
              const { transcriptNumber, purpose } = describeTranscription(
                assignment.transcriptionId,
                assignment.criteriaId
              );
              return (
                transcriptNumber.toLowerCase().includes(query) ||
                purpose.toLowerCase().includes(query)
              );
            });
          }
        }
        return {
          ...g,
          filteredAssignments: matchingAssignments,
        };
      })
      .filter(g => g.filteredAssignments.length > 0)
      .sort((a, b) => a.criterionName.localeCompare(b.criterionName));

    return {
      groupedData: result,
      criteriaCounts: {
        notDone: notDoneCriteriaCount,
        done: doneCriteriaCount,
        all: totalCriteriaCount,
      },
    };
  }, [
    assignments,
    criteriaMap,
    completionFilter,
    searchQuery,
    describeTranscription,
    matchesDateFilter,
  ]);

  // All visible assignment IDs across all groups
  const allVisibleIds = useMemo(() => {
    return groupedData.flatMap(g => g.filteredAssignments.map(a => a.id));
  }, [groupedData]);

  const allVisibleSelected =
    allVisibleIds.length > 0 && allVisibleIds.every(id => selectedAssignmentIds.includes(id));
  const someVisibleSelected =
    allVisibleIds.some(id => selectedAssignmentIds.includes(id)) && !allVisibleSelected;

  // Toggle selection of all visible
  const handleToggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedAssignmentIds(prev => prev.filter(id => !allVisibleIds.includes(id)));
    } else {
      setSelectedAssignmentIds(prev => Array.from(new Set([...prev, ...allVisibleIds])));
    }
  };

  // Toggle selection for a specific group
  const handleToggleSelectGroup = (groupIds: string[]) => {
    const allGroupSelected = groupIds.every(id => selectedAssignmentIds.includes(id));
    if (allGroupSelected) {
      setSelectedAssignmentIds(prev => prev.filter(id => !groupIds.includes(id)));
    } else {
      setSelectedAssignmentIds(prev => Array.from(new Set([...prev, ...groupIds])));
    }
  };

  // Toggle single selection
  const handleToggleSelectRow = (id: string) => {
    setSelectedAssignmentIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Single-row done toggle
  const handleToggleSingleDone = async (assignment: CriterionTranscriptionAssignment) => {
    const nextDone = !assignment.done;
    setUpdatingId(assignment.id);
    try {
      const updated = await api.updateAssignmentDone(assignment.id, nextDone);
      const updatedList = assignments.map(a => (a.id === updated.id ? updated : a));
      onAssignmentsUpdated(updatedList);
      showNotification?.(`Assignment marked as ${nextDone ? 'done' : 'not done'}`, 'success');
    } catch (err) {
      console.error('Failed to update assignment status:', err);
      showNotification?.('Failed to update assignment status', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  // Bulk done toggle
  const handleBulkToggleDone = async (nextDone: boolean) => {
    if (selectedAssignmentIds.length === 0) return;
    setIsUpdating(true);
    try {
      const result = await api.bulkUpdateAssignmentsDone(selectedAssignmentIds, nextDone);
      const updatedMap = new Map(result.assignments.map(a => [a.id, a]));
      const updatedList = assignments.map(a => updatedMap.get(a.id) ?? a);
      onAssignmentsUpdated(updatedList);
      showNotification?.(
        `Marked ${result.updatedCount} assignment${result.updatedCount === 1 ? '' : 's'} as ${
          nextDone ? 'done' : 'not done'
        }`,
        'success'
      );
      setSelectedAssignmentIds([]);
    } catch (err) {
      console.error('Failed to bulk update assignments:', err);
      showNotification?.('Failed to update selected assignments', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  // Navigate to an assignment
  const handleOpenAssignment = (assignment: CriterionTranscriptionAssignment) => {
    const crit = criteriaMap.get(assignment.criteriaId);
    const trans = transcriptionsMap.get(assignment.transcriptionId);
    if (crit && trans) {
      onSelectAssignment(crit, trans);
      onClose();
    }
  };

  const isGroupExpanded = useCallback(
    (criterionId: string) => {
      if (searchQuery.trim()) return true;
      return expandedGroupIds.has(criterionId);
    },
    [expandedGroupIds, searchQuery]
  );

  const toggleGroupExpanded = useCallback((criterionId: string) => {
    setExpandedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(criterionId)) {
        next.delete(criterionId);
      } else {
        next.add(criterionId);
      }
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    setExpandedGroupIds(new Set(groupedData.map(g => g.criterionId)));
  }, [groupedData]);

  const handleCollapseAll = useCallback(() => {
    setExpandedGroupIds(new Set());
  }, []);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 650, md: 800 },
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          overflow: 'hidden',
          height: '100%',
        },
      }}
    >
      {/* Top Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
            My Assignments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalCount === 0
              ? 'No assignments assigned to you'
              : `${formatCount(doneCount)} of ${formatCount(totalCount)} done (${overallPct}%) · ${formatCount(
                  notDoneCount
                )} remaining`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close assignments drawer">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ width: '100%' }}>
        <LinearProgress
          variant="determinate"
          value={overallPct}
          color={overallPct === 100 ? 'success' : 'primary'}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>

      {/* Filters and Search Bar */}
      <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Completion Filter */}
          <ToggleButtonGroup
            size="small"
            value={completionFilter}
            exclusive
            onChange={(_, val) => val && setCompletionFilter(val)}
            aria-label="Filter assignments by completion"
          >
            <ToggleButton
              value="not_done"
              aria-label="Not done assignments"
              sx={{ py: 0.5, px: 1.25, fontSize: '0.8rem' }}
            >
              Not Done ({criteriaCounts.notDone})
            </ToggleButton>
            <ToggleButton
              value="done"
              aria-label="Done assignments"
              sx={{ py: 0.5, px: 1.25, fontSize: '0.8rem' }}
            >
              Done ({criteriaCounts.done})
            </ToggleButton>
            <ToggleButton
              value="all"
              aria-label="All assignments"
              sx={{ py: 0.5, px: 1.25, fontSize: '0.8rem' }}
            >
              All ({criteriaCounts.all})
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Date Filter Dropdown */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="date-filter-label" sx={{ fontSize: '0.8125rem' }}>
              Assigned Date
            </InputLabel>
            <Select
              labelId="date-filter-label"
              value={dateFilter}
              label="Assigned Date"
              onChange={e => setDateFilter(e.target.value as AssignmentDateFilter)}
              sx={{ fontSize: '0.8125rem' }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.8125rem' }}>
                All Time
              </MenuItem>
              <MenuItem value="today" sx={{ fontSize: '0.8125rem' }}>
                Today
              </MenuItem>
              <MenuItem value="last7" sx={{ fontSize: '0.8125rem' }}>
                Last 7 Days
              </MenuItem>
              <MenuItem value="last30" sx={{ fontSize: '0.8125rem' }}>
                Last 30 Days
              </MenuItem>
              <MenuItem value="custom" sx={{ fontSize: '0.8125rem' }}>
                Custom Range
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Custom Date Range Picker */}
        {dateFilter === 'custom' && (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={customStartDate}
              onChange={e => setCustomStartDate(e.target.value)}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={customEndDate}
              onChange={e => setCustomEndDate(e.target.value)}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
            />
          </Box>
        )}

        {/* Search Input */}
        <TextField
          size="small"
          placeholder="Search by criterion name, transcript number, purpose..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            sx: { fontSize: '0.8125rem' },
          }}
          fullWidth
        />
      </Paper>

      {/* Multi-Select & Bulk Actions Bar */}
      {allVisibleIds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 0.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              size="small"
              checked={allVisibleSelected}
              indeterminate={someVisibleSelected}
              onChange={handleToggleSelectAll}
              inputProps={{ 'aria-label': 'Select all visible assignments' }}
              sx={{ p: 0.25 }}
            />
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
              {selectedAssignmentIds.length > 0
                ? `${selectedAssignmentIds.length} selected`
                : 'Select all'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={selectedAssignmentIds.length === 0 || isUpdating}
              onClick={() => handleBulkToggleDone(true)}
              sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}
            >
              {isUpdating ? <CircularProgress size={14} sx={{ mr: 0.75 }} /> : null}
              Mark Done
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={selectedAssignmentIds.length === 0 || isUpdating}
              onClick={() => handleBulkToggleDone(false)}
              sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}
            >
              Mark Not Done
            </Button>
          </Box>
        </Box>
      )}

      {/* Expand/Collapse All and Count bar */}
      {groupedData.length > 0 && (
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5 }}
        >
          <Typography variant="caption" color="text.secondary">
            {groupedData.length} {groupedData.length === 1 ? 'criterion' : 'criteria'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              onClick={handleExpandAll}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                p: 0,
                minWidth: 'auto',
                color: 'text.secondary',
              }}
            >
              Expand all
            </Button>
            <Typography variant="caption" color="text.disabled">
              ·
            </Typography>
            <Button
              size="small"
              onClick={handleCollapseAll}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                p: 0,
                minWidth: 'auto',
                color: 'text.secondary',
              }}
            >
              Collapse all
            </Button>
          </Box>
        </Box>
      )}

      {/* Criterion Groups List */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pr: 0.5,
        }}
      >
        {totalCount === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            You do not currently have any assignments.
          </Alert>
        ) : groupedData.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            {completionFilter === 'not_done'
              ? 'All caught up! You have no criteria with open or started assignments matching the selected filters.'
              : completionFilter === 'done'
                ? 'No criteria with 100% completed assignments match the selected filters.'
                : 'No assignments match the selected filters.'}
          </Alert>
        ) : (
          groupedData.map(group => {
            const isExpanded = isGroupExpanded(group.criterionId);
            const groupFilteredIds = group.filteredAssignments.map(a => a.id);
            const allGroupSelected =
              groupFilteredIds.length > 0 &&
              groupFilteredIds.every(id => selectedAssignmentIds.includes(id));
            const someGroupSelected =
              groupFilteredIds.some(id => selectedAssignmentIds.includes(id)) && !allGroupSelected;

            return (
              <Paper
                key={group.criterionId}
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  overflow: 'hidden',
                  borderColor: 'divider',
                  flexShrink: 0,
                }}
              >
                {/* Group Header - Reduced height */}
                <Box
                  onClick={() => toggleGroupExpanded(group.criterionId)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1,
                    py: 0.35,
                    minHeight: 28,
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background-color 0.15s',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                  aria-expanded={isExpanded}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGroupExpanded(group.criterionId);
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    {groupFilteredIds.length > 0 && (
                      <Checkbox
                        size="small"
                        checked={allGroupSelected}
                        indeterminate={someGroupSelected}
                        onClick={e => e.stopPropagation()}
                        onChange={() => handleToggleSelectGroup(groupFilteredIds)}
                        inputProps={{
                          'aria-label': `Select all visible assignments for ${group.criterionName}`,
                        }}
                        sx={{ p: 0.2 }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.785rem',
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {group.criterionName}
                    </Typography>
                    <Chip
                      size="small"
                      label={group.granularity}
                      variant="outlined"
                      sx={{
                        textTransform: 'capitalize',
                        height: 16,
                        fontSize: '0.625rem',
                        px: 0.2,
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                    <Chip
                      size="small"
                      color={group.groupDone === group.total ? 'success' : 'default'}
                      label={`${group.groupDone}/${group.total} done (${group.groupPct}%)`}
                      sx={{ height: 18, fontSize: '0.675rem' }}
                    />
                    <ExpandMoreIcon
                      sx={{
                        fontSize: '1rem',
                        color: 'text.secondary',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </Box>
                </Box>

                {/* Collapsible Assignment Table - Reduced row height */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Divider />
                  {group.filteredAssignments.length === 0 ? (
                    <Box sx={{ p: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        No assignments in this group match the filter.
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow
                            sx={{
                              bgcolor: 'grey.50',
                              '& th': { verticalAlign: 'middle', py: 0.4 },
                            }}
                          >
                            <TableCell
                              sx={{
                                py: 0.4,
                                px: 0.75,
                                width: 36,
                                textAlign: 'center',
                                verticalAlign: 'middle',
                              }}
                            />
                            <TableCell
                              sx={{
                                py: 0.4,
                                px: 0.75,
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                color: 'text.secondary',
                                width: 95,
                              }}
                            >
                              Transcription
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.4,
                                px: 0.75,
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                color: 'text.secondary',
                              }}
                            >
                              Purpose
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.4,
                                px: 0.75,
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                color: 'text.secondary',
                                width: 80,
                              }}
                            >
                              Assigned
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.4,
                                px: 0.75,
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                color: 'text.secondary',
                                width: 75,
                              }}
                            >
                              Progress
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                py: 0.4,
                                px: 0.75,
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                color: 'text.secondary',
                                width: 50,
                              }}
                            >
                              Done
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                py: 0.4,
                                px: 0.75,
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                color: 'text.secondary',
                                width: 60,
                              }}
                            >
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.filteredAssignments.map(assignment => {
                            const isSelected = selectedAssignmentIds.includes(assignment.id);
                            const { transcriptNumber, purpose } = describeTranscription(
                              assignment.transcriptionId,
                              assignment.criteriaId
                            );
                            const isCurrentUpdating = updatingId === assignment.id;

                            return (
                              <TableRow
                                key={assignment.id}
                                hover
                                selected={isSelected}
                                sx={{
                                  height: 36,
                                  opacity: assignment.done ? 0.75 : 1,
                                  '&:hover': { opacity: 1 },
                                  '& td': { verticalAlign: 'middle', py: 0.75 },
                                }}
                              >
                                <TableCell
                                  sx={{
                                    py: 0.75,
                                    px: 0.75,
                                    width: 36,
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={isSelected}
                                      onChange={() => handleToggleSelectRow(assignment.id)}
                                      inputProps={{
                                        'aria-label': `Select assignment for transcript ${transcriptNumber}`,
                                      }}
                                      sx={{
                                        p: 0.25,
                                      }}
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ py: 0.75, px: 0.75, verticalAlign: 'middle' }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, fontSize: '0.75rem', lineHeight: 1.2 }}
                                  >
                                    {transcriptNumber}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.75, px: 0.75, verticalAlign: 'middle' }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.75rem',
                                      lineHeight: 1.2,
                                      color: 'text.primary',
                                    }}
                                  >
                                    {purpose}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.75, px: 0.75, verticalAlign: 'middle' }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                                  >
                                    {formatShortDate(assignment.assignedDate)}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.75, px: 0.75, verticalAlign: 'middle' }}>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color={assignment.status === 'started' ? 'info' : 'default'}
                                    label={assignment.status === 'started' ? 'Started' : 'Open'}
                                    sx={{ height: 18, fontSize: '0.625rem' }}
                                  />
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ py: 0.75, px: 0.75, verticalAlign: 'middle' }}
                                >
                                  <Tooltip
                                    title={assignment.done ? 'Mark as not done' : 'Mark as done'}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <Switch
                                        size="small"
                                        color="success"
                                        checked={assignment.done}
                                        disabled={isCurrentUpdating || isUpdating}
                                        onChange={() => handleToggleSingleDone(assignment)}
                                        inputProps={{
                                          'aria-label': `Toggle done for transcript ${transcriptNumber}`,
                                        }}
                                        sx={{ transform: 'scale(0.72)', m: -0.25 }}
                                      />
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ py: 0.75, px: 0.75, verticalAlign: 'middle' }}
                                >
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleOpenAssignment(assignment)}
                                    sx={{
                                      py: 0.2,
                                      px: 0.75,
                                      minWidth: 42,
                                      height: 22,
                                      fontSize: '0.6875rem',
                                    }}
                                  >
                                    Label
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Collapse>
              </Paper>
            );
          })
        )}
      </Box>
    </Drawer>
  );
}

function formatCount(n: number): string {
  return n.toLocaleString();
}
